import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Paper, Grid, Chip, Divider } from '@mui/material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import jobWorksApi from '../api/jobWorks';
import { JobWorkWithDetails, JobWorkReceipt, JobWorkChallanData } from '../components/jobwork/types';
import { JobWorkReceivedItem } from '../api/jobWorks';
import JobWorkHeader from '../components/jobwork/JobWorkHeader';
import JobWorkMaterials from '../components/jobwork/JobWorkMaterials';
import JobWorkReceipts from '../components/jobwork/JobWorkReceipts';
import JobWorkChallan from '../components/jobwork/JobWorkChallan';
import ReceiptDialog from '../components/jobwork/ReceiptDialog';
import InvoiceDialog from '../components/jobwork/InvoiceDialog';
import JobWorkDesignDetails from '../components/jobwork/JobWorkDesignDetails';
import { generateJobWorkChallan, generateJobWorkReceipt } from '../utils/pdfGenerator';
import { ArrowBack } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const JobWorkDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/job-works';
  const { enqueueSnackbar } = useSnackbar();
  
  const [jobWork, setJobWork] = useState<JobWorkWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Receipt dialog state
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [editingReceiptId, setEditingReceiptId] = useState<number | undefined>(undefined);
  const [editingReceipt, setEditingReceipt] = useState<JobWorkReceipt | undefined>(undefined);
  
  // Invoice dialog state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  // Confirmation dialog for marking as complete
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [validationWarning, setValidationWarning] = useState<string | null>(null);
  
  // Load job work data
  useEffect(() => {
    const fetchJobWork = async () => {
      if (!id) return;
      
      try {
        console.log('Fetching job work with ID:', id);
        console.log('Token in localStorage:', localStorage.getItem('token') ? 'Present' : 'Missing');
        
        setLoading(true);
        const response = await jobWorksApi.getJobWorkById(parseInt(id));
        console.log('API Response:', response);
        
        if (!response) {
          console.error('No response from API');
          setError('No response received from server');
          return;
        }
        
        console.log('Job work data received with actual API data:', response.data);
        
        // Transform receivedItems into receipts if needed
        const jobWorkData = { ...response.data };
        
        // Don't create synthetic receipts - just directly use what comes from the API
        console.log('Job work data received with actual API data:', jobWorkData);
        
        setJobWork(jobWorkData);
      } catch (err: any) {
        console.error('Error fetching job work:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError(err.response?.data?.error || 'Failed to load job work details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobWork();
  }, [id]);

  // Handle print challan
  const handlePrintChallan = async () => {
    if (!jobWork) return;
    
    try {
      setLoading(true);
      // Auto-issue if still pending
      if (jobWork.status === 'PENDING') {
        await jobWorksApi.issue(jobWork.id, { issue_date: new Date().toISOString() });
        const refreshed = await jobWorksApi.getJobWorkById(jobWork.id);
        setJobWork(refreshed.data);
      }
      
      // Prepare size data from design details
      const sizes = jobWork.designDetails 
        ? jobWork.designDetails.map(detail => ({
            size: detail.size || '',
            quantity: detail.pieces || 0
          }))
        : [];
      
      // Calculate total quantity
      const totalQuantity = sizes.reduce((sum, item) => sum + (item.quantity || 0), 0);
      
      // Transform materials with proper typing
      console.log('Job work materials data:', JSON.stringify(jobWork.materials, null, 2));
      
      const materials = jobWork.materials.map(material => {
        console.log('Material details for material:', material.id, material);
        return {
          materialCode: material.material?.material_code || 'N/A',
          materialType: material.material?.material_type || 'N/A',
          quantity: material.quantity || 0,
          uom: material.uom || 'PCS',
          rate: material.rate || (material as any).processingRate || 0,
          materialImage: material.material?.image_url || undefined,
          comments: material.comments || '' // Taka details
        };
      });
      
      console.log('Transformed materials for PDF:', materials);
      
      // Prepare data for the challan
      const challanData: any = {
        jobWorkCode: jobWork.jobWorkCode || '',
        designNo: jobWork.designNo || '',
        issueDate: jobWork.issueDate ? format(new Date(jobWork.issueDate), 'dd/MM/yyyy') : 'N/A',
        expectedReturn: jobWork.expectedReturn ? format(new Date(jobWork.expectedReturn), 'dd/MM/yyyy') : 'N/A',
        jobberName: jobWork.vendor?.registered_name || jobWork.vendor?.alias || 'Unknown',
        jobberPhone: jobWork.vendor?.phone || 'N/A',
        jobberAddress: jobWork.vendor?.address || 'N/A',
        jobType: {
          name: jobWork.jobType?.name || 'N/A',
          description: jobWork.jobType?.description
        },
        vendor: {
          registered_name: jobWork.vendor?.registered_name || 'Unknown',
          alias: jobWork.vendor?.alias || '',
          address: jobWork.vendor?.address || '',
          phone: jobWork.vendor?.phone || ''
        },
        companyName: (jobWork.company && jobWork.company.name) || 'STITCHFLO',
        materials: materials,
        designDetails: jobWork.designDetails.map(detail => ({
          garmentPart: detail.garmentPart || '',
          size: detail.size || '',
          pieces: detail.pieces || 0,
          rate: detail.rate || 0
        })),
        sizes: sizes,
        totalQuantity: totalQuantity,
        notes: jobWork.notes || undefined,
        designImage: jobWork.design?.image_url || undefined,
        companyDetails: {
          brandName: jobWork.company?.brand_name || '',
          name: jobWork.company?.name || '',
          address: jobWork.company?.address || '',
          contactNumber: jobWork.company?.contact_number || ''
        },
        processMaterial: jobWork.processMaterial,
        isInternalVendor: jobWork.vendor?.is_internal === true || (jobWork.vendor as any)?.vendor_type === 'INTERNAL',
        image1_url: (jobWork as any).image1_url || undefined,
        image2_url: (jobWork as any).image2_url || undefined,
      };
      
      // Generate and download the challan
      await generateJobWorkChallan(challanData);
      
      enqueueSnackbar('Job work challan generated successfully', { variant: 'success' });
      
      } catch (err) {
      console.error('Error generating challan:', err);
      setError('Failed to generate challan. Please try again.');
      enqueueSnackbar('Failed to generate challan', { variant: 'error' });
      } finally {
        setLoading(false);
    }
  };

  // Explicitly issue job when status is PENDING
  const handleIssueJob = async () => {
    if (!jobWork) return;
    try {
      setLoading(true);
      await jobWorksApi.issue(jobWork.id, { issue_date: new Date().toISOString() });
      const refreshed = await jobWorksApi.getJobWorkById(jobWork.id);
      setJobWork(refreshed.data);
      enqueueSnackbar('Job has been issued', { variant: 'success' });
    } catch (err) {
      console.error('Error issuing job:', err);
      enqueueSnackbar('Failed to issue job', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Mark job as processing (internal vendors only)
  const handleProcessJob = async () => {
    if (!jobWork) return;
    try {
      setLoading(true);
      await jobWorksApi.markAsProcessing(jobWork.id);
      const refreshed = await jobWorksApi.getJobWorkById(jobWork.id);
      setJobWork(refreshed.data);
      enqueueSnackbar('Job marked as PROCESSING', { variant: 'success' });
    } catch (err) {
      console.error('Error marking job as processing:', err);
      enqueueSnackbar('Failed to mark job as PROCESSING', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to safely format dates
  const safeFormatDate = (dateInput: string | number | Date | null | undefined, formatString: string): string => {
    if (!dateInput) return 'N/A';
    try {
      const date = new Date(dateInput);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date value encountered:', dateInput);
        return 'N/A';
      }
      return format(date, formatString);
    } catch (error) {
      console.error('Error formatting date:', dateInput, error);
      return 'N/A';
    }
  };

  // Handle print receipt
  const handlePrintReceipt = async (receipt: JobWorkReceivedItem) => {
    if (!jobWork) return;

    try {
      setLoading(true);

      console.log('Receipt being printed:', receipt);

      let formattedItem: any;

      if (jobWork.processMaterial) {
        const materialData = jobWork.materials.find(m => m.materialId === receipt.materialId);
        const materialInfo = materialData?.material;
        const quantity = receipt.materialQuantity || 0;
        const rate = materialData?.rate || 0; 
        const uom = materialData?.uom || 'N/A';

        formattedItem = {
          garmentPart: materialInfo?.material_code || 'N/A',
          size: materialInfo?.material_type || 'N/A',
          quantity: quantity,
          uom: uom,
          rate: rate,
          amount: quantity * rate,
        };
      } else {
        const quantity = receipt.pieceCount || 0;
        const rate = receipt.processingRate || 0;

        formattedItem = {
          garmentPart: receipt.garmentPart || 'N/A',
          size: receipt.size || 'N/A',
          quantity: quantity,
          uom: 'PCS',
          rate: rate,
          amount: quantity * rate,
        };
      }

      // Prepare the payload for the PDF generator using jobWork for general details and safe date formatting
      const pdfPayload = {
        id: receipt.id,
        jobWorkId: jobWork.id,
        receiveDate: safeFormatDate(receipt.createdAt, 'dd/MM/yyyy'), // Use safe formatting
        invoiceNumber: jobWork.invoiceNumber || '', 
        invoiceDate: safeFormatDate(jobWork.invoiceDate, 'dd/MM/yyyy'), // Use safe formatting
        invoiceAmount: jobWork.invoiceAmount || 0,
        taxAmount: jobWork.taxAmount || 0,
        isSampleReceived: jobWork.isSampleReceived || false,
        receivedItems: [formattedItem], 
        createdAt: receipt.createdAt, 
        updatedAt: receipt.updatedAt, 
      };

      // Generate and download the receipt PDF
      await generateJobWorkReceipt(
        pdfPayload,
        jobWork.jobWorkCode || '',
        jobWork.vendor?.registered_name || jobWork.vendor?.alias || 'Unknown Vendor',
        jobWork.designNo || '',
        jobWork.jobType?.name || 'N/A'
      );

      enqueueSnackbar('Receipt PDF generated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error printing receipt:', err);
      enqueueSnackbar('Failed to generate receipt PDF', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle add receipt
  const handleAddReceipt = () => {
    if (!jobWork) return;
    
    setEditingReceiptId(undefined);
    setReceiveDialogOpen(true);
  };

  // Handle edit receipt
  const handleEditReceipt = (receipt: JobWorkReceivedItem) => {
    setEditingReceiptId(receipt.id);
    setReceiveDialogOpen(true);
  };
  
  // Handle save receipt
  const handleSaveReceipt = async (receipt: Partial<JobWorkReceipt>) => {
    if (!id || !jobWork) return;
    
    try {
      setLoading(true);
      
      if (editingReceiptId) {
        // Update existing receipt
        await jobWorksApi.updateReceipt(parseInt(id), editingReceiptId, receipt);
        enqueueSnackbar('Receipt updated successfully', { variant: 'success' });
      } else {
        // Create new receipt
        await jobWorksApi.createReceipt(parseInt(id), receipt);
        enqueueSnackbar('Receipt created successfully', { variant: 'success' });
      }
      
      // Reload job work data
      const response = await jobWorksApi.getJobWorkById(parseInt(id));
      setJobWork(response.data);
      setReceiveDialogOpen(false);
      setEditingReceiptId(undefined);
      setEditingReceipt(undefined);
      
    } catch (err) {
      console.error('Error saving receipt:', err);
      enqueueSnackbar('Failed to save receipt', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete receipt
  const handleDeleteReceipt = async (receiptId: number) => {
    if (!id || !jobWork) {
      console.error("Missing job work ID or job work data");
      enqueueSnackbar('Cannot delete receipt: missing job work information', { variant: 'error' });
      return;
    }
    
    // Ensure receiptId is a valid number
    if (!receiptId || isNaN(receiptId) || receiptId <= 0) {
      console.error("Invalid receipt ID:", receiptId);
      enqueueSnackbar('Cannot delete receipt: invalid receipt ID', { variant: 'error' });
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Deleting receipt with ID:', receiptId, 'from job work:', id);
      
      // Find the receipt in the job work data
      const receipt = jobWork.receivedItems?.find(r => r.id === receiptId);
      
      if (!receipt) {
        console.error("Receipt not found in job work data");
        enqueueSnackbar('Receipt not found', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      const jobWorkIdNum = parseInt(id);
      
      if (isNaN(jobWorkIdNum) || jobWorkIdNum <= 0) {
        console.error("Invalid job work ID format:", id);
        enqueueSnackbar('Cannot delete receipt: invalid job work ID format', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Delete the receipt
      await jobWorksApi.deleteReceipt(jobWorkIdNum, receiptId);
      enqueueSnackbar('Receipt deleted successfully', { variant: 'success' });
      
      // Reload job work data
      const response = await jobWorksApi.getJobWorkById(jobWorkIdNum);
      setJobWork(response.data);
      
    } catch (err) {
      console.error('Error deleting receipt:', err);
      enqueueSnackbar('Failed to delete receipt', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setReceiveDialogOpen(false);
    setEditingReceiptId(undefined);
    setEditingReceipt(undefined);
  };

  // Check if the job work can be completed
  const validateCompletion = () => {
    if (!jobWork) return { canComplete: false, warning: "Job work data not available" };
    
    let warning: string | null = null;
    let canComplete = true;
    
    const receivedItems = jobWork.receivedItems || [];
    
    if (jobWork.processMaterial) {
      // Process material validation - check materials received against materials issued
      const materials = jobWork.materials;
      
      // Group received items by material ID
      const receivedByMaterial: Record<string, number> = {};
      
      receivedItems.forEach(item => {
        const materialId = item.materialId?.toString() || '';
        if (!receivedByMaterial[materialId]) {
          receivedByMaterial[materialId] = 0;
        }
        receivedByMaterial[materialId] += (item.materialQuantity || 0);
      });
      
      // Check each material
      for (const material of materials) {
        const materialId = material.materialId?.toString() || '';
        const received = receivedByMaterial[materialId] || 0;
        const issuedQty = material.quantity || 0;
        
        if (received < issuedQty) {
          warning = `Material not fully received. Issued: ${issuedQty}, Received: ${received} for Material ID: ${material.materialId}`;
          canComplete = false;
          break;
        }
      }
    } else {
      // Design details validation - check pieces received against pieces issued
      const designDetails = jobWork.designDetails;
      
      // Group received items by design part and size
      const receivedByPartAndSize: Record<string, { received: number, defect: number, shortage: number }> = {};
      
      receivedItems.forEach(item => {
        const key = `${item.garmentPart || ''}-${item.size || ''}`;
        if (!receivedByPartAndSize[key]) {
          receivedByPartAndSize[key] = { received: 0, defect: 0, shortage: 0 };
        }
        receivedByPartAndSize[key].received += (item.pieceCount || 0);
        receivedByPartAndSize[key].defect += (item.defectQuantity || 0);
        receivedByPartAndSize[key].shortage += (item.shortageQuantity || 0);
      });
      
      // Check each design detail
      for (const detail of designDetails) {
        const key = `${detail.garmentPart || ''}-${detail.size || ''}`;
        const received = receivedByPartAndSize[key] || { received: 0, defect: 0, shortage: 0 };
        const issuedQty = detail.pieces || 0;
        const totalReceived = received.received + received.defect + received.shortage;
        
        if (totalReceived < issuedQty) {
          warning = `Some items were not fully received. Issued: ${issuedQty}, Received: ${totalReceived} for ${detail.garmentPart || 'N/A'} - ${detail.size || 'N/A'}`;
          canComplete = false;
          break;
        }
      }
    }
    
    return { canComplete, warning };
  };

  // Handle mark as complete
  const handleMarkComplete = () => {
    const validation = validateCompletion();
    setValidationWarning(validation.warning);
    setCompleteDialogOpen(true);
  };
  
  // Confirm job work completion
  const handleConfirmComplete = async () => {
    if (!id || !jobWork) return;
    
    try {
      setLoading(true);
      
      // Mark job work as completed
      await jobWorksApi.markAsComplete(parseInt(id));
      
      // Reload job work data
      const response = await jobWorksApi.getJobWorkById(parseInt(id));
      setJobWork(response.data);
      
      enqueueSnackbar('Job work marked as completed', { variant: 'success' });
      setCompleteDialogOpen(false);
    } catch (err) {
      console.error('Error completing job work:', err);
      enqueueSnackbar('Failed to mark job work as completed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle invoice saved - reload job work data
  const handleInvoiceSaved = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await jobWorksApi.getJobWorkById(parseInt(id));
      setJobWork(response.data);
    } catch (err) {
      console.error('Error reloading job work after invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if invoiced
  const isInvoiced = !!(jobWork?.invoiceNumber && jobWork?.invoiceImageUrl);

  if (loading) {
  return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress />
          </Box>
    );
  }
  
  if (error) {
                      return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!jobWork) {
                      return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">Job work not found</Alert>
      </Container>
    );
  }
                            
                        return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
                                <Button
                                  variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(returnTo)}
                                >
          Back to List
                                </Button>
          </Box>
      <JobWorkHeader 
        jobWork={jobWork}
        onPrintChallan={handlePrintChallan}
        onAddReceipt={handleAddReceipt}
        onMarkComplete={handleMarkComplete}
        onProcessJob={handleProcessJob}
        onIssueJob={handleIssueJob}
        onAddInvoice={() => setInvoiceDialogOpen(true)}
        onEdit={() => navigate(`/job-works/${jobWork.id}/edit?returnTo=${encodeURIComponent(returnTo)}`)}
        loading={loading}
      />
      
      <JobWorkMaterials 
        materials={jobWork.materials}
        processMaterial={jobWork.processMaterial}
      />
      
      {/* Show design details even when processing material; hide rates in that case */}
      <JobWorkDesignDetails 
        designDetails={jobWork.designDetails}
        hideRates={jobWork.processMaterial}
      />
      
      <JobWorkReceipts 
        receipts={jobWork.receivedItems || []}
        onEditReceipt={handleEditReceipt}
        onPrintReceipt={handlePrintReceipt}
        onDeleteReceipt={handleDeleteReceipt}
        isCompleted={jobWork.status === 'COMPLETED'}
        processMaterial={jobWork.processMaterial}
        jobWork={jobWork}
      />
      
      {/* Invoice Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Invoice Details
          </Typography>
          {jobWork.status !== 'CANCELLED' && (
            <Button
              variant={isInvoiced ? 'outlined' : 'contained'}
              color={isInvoiced ? 'primary' : 'warning'}
              startIcon={<ReceiptLongIcon />}
              onClick={() => setInvoiceDialogOpen(true)}
              size="small"
            >
              {isInvoiced ? 'Update Invoice' : 'Add Invoice'}
            </Button>
          )}
        </Box>
        {isInvoiced ? (
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
              <Typography variant="body1" fontWeight={600}>{jobWork.invoiceNumber}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
              <Typography variant="body1">
                {jobWork.invoiceDate ? format(new Date(jobWork.invoiceDate), 'dd/MM/yyyy') : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="text.secondary">Amount</Typography>
              <Typography variant="body1" fontWeight={600}>₹{(jobWork.invoiceAmount || 0).toLocaleString('en-IN')}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="text.secondary">Tax</Typography>
              <Typography variant="body1">₹{(jobWork.taxAmount || 0).toLocaleString('en-IN')}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography variant="body1" fontWeight={600} color="success.main">
                ₹{(jobWork.totalAmount || 0).toLocaleString('en-IN')}
              </Typography>
            </Grid>
            {jobWork.invoiceImageUrl && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Attachment:</Typography>
                  <Button size="small" href={jobWork.invoiceImageUrl} target="_blank" variant="text">
                    View Invoice Copy
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Chip
              label="Not Invoiced"
              color="warning"
              variant="outlined"
              size="small"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Invoice details have not been added yet. Click "Add Invoice" to add invoice number, amount and attachment.
            </Typography>
          </Box>
        )}
      </Paper>

      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        jobWorkId={parseInt(id || '0')}
        jobWorkCode={jobWork.jobWorkCode}
        existingInvoice={isInvoiced ? {
          invoiceNumber: jobWork.invoiceNumber || undefined,
          invoiceDate: jobWork.invoiceDate || undefined,
          invoiceAmount: jobWork.invoiceAmount || undefined,
          taxAmount: jobWork.taxAmount || undefined,
          invoiceImageUrl: jobWork.invoiceImageUrl || undefined
        } : undefined}
        onSave={handleInvoiceSaved}
      />

      <ReceiptDialog
        open={receiveDialogOpen}
        onClose={handleCloseDialog}
        jobWorkId={parseInt(id || '0')}
        designNo={jobWork.designNo || ''}
        designDetails={jobWork.designDetails.map(detail => ({
          garmentPart: detail.garmentPart,
          size: detail.size,
          pieces: detail.pieces,
          rate: detail.rate || 0
        }))}
        editingReceiptId={editingReceiptId}
        onSave={handleSaveReceipt}
        processMaterial={jobWork.processMaterial}
        materials={jobWork.materials.map(material => ({
          materialId: material.materialId,
          materialCode: material.material?.material_code || '',
          materialType: material.material?.material_type || '',
          quantity: material.quantity,
          uom: material.uom,
          rate: material.rate || 0
        }))}
      />
      
      {/* Completion Confirmation Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        aria-labelledby="complete-dialog-title"
      >
        <DialogTitle id="complete-dialog-title">
          Confirm Mark as Complete
        </DialogTitle>
        <DialogContent>
          {validationWarning ? (
            <>
              <DialogContentText color="warning.main">
                Warning: {validationWarning}
              </DialogContentText>
              <DialogContentText sx={{ mt: 2 }}>
                Do you still want to mark this job work as complete? Once completed, no further edits will be allowed.
              </DialogContentText>
            </>
          ) : (
            <DialogContentText>
              Are you sure you want to mark this job work as complete? Once completed, no further edits will be allowed.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmComplete}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Mark as Complete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JobWorkDetailsPage;